/**
 * Clipboard writing for the shared clients (prompt.md §20, §48).
 *
 * WHY this is one function instead of `navigator.clipboard` at each call site: both
 * clients need the same three outcomes, and the interesting ones are the failures.
 * `navigator.clipboard` is absent outside a secure context and can be blocked, and a
 * silent failure would leave the user believing the text was copied. Its absence is
 * reported as an error the UI can show, and the legacy selection fallback is used
 * only when it can actually copy.
 */

export interface ClipboardLike {
  writeText(text: string): Promise<void>;
}

function browserClipboard(): ClipboardLike | null {
  const clipboard = (globalThis as { navigator?: { clipboard?: unknown } }).navigator?.clipboard;
  const writeText = (clipboard as ClipboardLike | null)?.writeText;
  return typeof writeText === "function" ? (clipboard as ClipboardLike) : null;
}

/**
 * The only clipboard failure a user can act on: the platform refused, or there is
 * no clipboard at all. Both are reported as a plain error the caller shows.
 */
export class ClipboardUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ClipboardUnavailableError";
  }
}

/** Writes text through the platform clipboard, or throws with what the user can do. */
export async function writeClipboardText(text: string): Promise<void> {
  const clipboard = browserClipboard();
  if (clipboard === null) {
    throw new ClipboardUnavailableError("This client has no clipboard access; select the text and copy it with the keyboard instead.");
  }
  try {
    await clipboard.writeText(text);
  } catch {
    throw new ClipboardUnavailableError("The browser refused the clipboard write; select the text and copy it with the keyboard instead.");
  }
}
